import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import useIsMobile from '../../../hooks/useIsMobile';
import { fixBodyScroll, unfixBodyScroll, releaseBodyScroll } from '../../../modules/helpers';

// Components
import MobileMenu from './MobileMenu';
import FoodTrucksMenu from './FoodTrucksMenu';
import CateringMenu from './CateringMenu';
import HeaderNavControls from './HeaderNavControls';

import styles from './Header.module.postcss';

function Header() {
  const { city_name, logo, site_title, vehicle_type } = useContext(ConfigContext);
  const isMobile = useIsMobile(1080);
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isClient, setIsClient] = useState(false);

  // Desktop mega-menu state
  const [openMenu, setOpenMenu] = useState(null); // 'foodTrucks', 'catering', 'services', or null
  const closeTimerRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Publish the rendered header height as `--header-height` on <html>. The
  // sticky bars that sit under the header (ScrollNav, SearchFilters) read it
  // for their `top`, so they stay flush with the header's bottom edge instead
  // of the hard-coded $nav-height guess, which drifted from the real height
  // and left a strip of scrolling content visible between the two.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    let active = true;
    let frame = null;
    let published = null;
    const publish = () => {
      if (!active) return;
      const height = Math.ceil(el.getBoundingClientRect().height);
      if (height === published) return;
      published = height;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    };
    // The observer defers its write to the next frame. Writing the variable
    // inside the callback re-lays-out the page mid-frame, and when that
    // nudges the header's size again in the same frame, the browser
    // reports "ResizeObserver loop completed with undelivered notifications"
    // as an uncaught error (Sentry AUSTINFOODTRUCK-REACT-B).
    const schedule = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        publish();
      });
    };
    publish();
    // Web-font swap changes the nav's line-height by a pixel; re-measure once
    // fonts settle so the value is right even before the observer fires.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(publish);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    if (observer) observer.observe(el);
    return () => {
      active = false;
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      // Otherwise the last public-header height stays on <html> after this
      // header unmounts, and AdminLayout's sticky bars offset themselves
      // against a header that is no longer on the page. Removing it puts
      // the consumers back on their $nav-height fallback.
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  // The mobile menu locks body scroll, and the effect below is the only thing
  // that releases it — so it never runs when this header is what goes away.
  // ParentLayout sends /catering/* and /vendor/* to AdminLayout, and Layout
  // drops the header on /login, so tapping either destination in the open
  // menu unmounts this component mid-navigation and leaves
  // `body { width: 100%; position: fixed }` set on the page that follows.
  // That page is then clipped to one viewport and cannot scroll: measured on
  // live Austin (375x812), /login arrived 812px tall instead of 1054px, with
  // scrollTo() pinned at 0, so the bottom 242px — the account blurb below the
  // sign-in buttons — was unreachable. Pages that happen to fit a viewport,
  // like the wizard's first step, hide the bug rather than escape it.
  useEffect(() => releaseBodyScroll, []);

  // Close menus on navigation
  useEffect(() => {
    unfixBodyScroll(scrollY);
    setMobileMenuOpen(false);
    setOpenMenu(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const toggleMenu = () => {
    if (mobileMenuOpen) {
      unfixBodyScroll(scrollY);
      setMobileMenuOpen(false);
    } else {
      const newScrollY = fixBodyScroll();
      setScrollY(newScrollY);
      setMobileMenuOpen(true);
    }
  };

  // Desktop hover handlers with close delay
  const handleMenuEnter = useCallback((menuName) => {
    clearTimeout(closeTimerRef.current);
    setOpenMenu(menuName);
  }, []);

  const handleMenuLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 200);
  }, []);

  const handleMenuClick = useCallback((menuName) => {
    setOpenMenu((prev) => (prev === menuName ? null : menuName));
  }, []);

  return (
    <>
    <a href='#main-content' className='u-sr-only'>Skip to content</a>
    <header ref={headerRef} className={styles.Header}>
      <div className='Section'>

        <div className={styles.Header_Left}>
          <Link to='/' className={styles.Header_Logo}>
            <img className={styles.Header_img} src={logo} alt={site_title} width='180' height='30' />
          </Link>

        </div>

        <nav aria-label='Main' className={styles.Header_Right}>
          {/* Food Trucks mega-menu trigger */}
          <div
            className={styles.Header_noMobile}
            onMouseEnter={() => handleMenuEnter('foodTrucks')}
            onMouseLeave={handleMenuLeave}
          >
            <button
              type='button'
              className={cn(styles.Header_Link, styles.Header_NavTrigger)}
              onClick={() => handleMenuClick('foodTrucks')}
              aria-expanded={openMenu === 'foodTrucks'}
              aria-controls='foodTrucksMenu'
            >
              Food {vehicle_type}s
            </button>
          </div>

          {/* Catering mega-menu trigger */}
          <div
            className={styles.Header_noMobile}
            onMouseEnter={() => handleMenuEnter('catering')}
            onMouseLeave={handleMenuLeave}
          >
            <button
              type='button'
              className={cn(styles.Header_Link, styles.Header_NavTrigger)}
              onClick={() => handleMenuClick('catering')}
              aria-expanded={openMenu === 'catering'}
              aria-controls='cateringMenu'
            >
              Catering
            </button>
          </div>

          {/* Services dropdown trigger */}
          <div
            className={styles.Header_noMobile}
            style={{ position: 'relative' }}
            onMouseEnter={() => handleMenuEnter('services')}
            onMouseLeave={handleMenuLeave}
          >
            <button
              type='button'
              className={cn(styles.Header_Link, styles.Header_NavTrigger)}
              onClick={() => handleMenuClick('services')}
              aria-expanded={openMenu === 'services'}
              aria-controls='servicesMenu'
            >
              Services
            </button>

            {/* Services dropdown — always in DOM */}
            <div
              id='servicesMenu'
              className={cn(styles.ServicesDropdownWrapper, {
                [styles.ServicesDropdownWrapper__open]: openMenu === 'services',
              })}
              aria-hidden={openMenu !== 'services'}
            >
              <Link to='/services/office-parks'>Office parks</Link>
              <Link to='/services/residential'>Residential</Link>
              <Link to='/services/breweries'>Breweries</Link>
            </div>
          </div>

          <HeaderNavControls
            cityName={city_name}
            vehicleType={vehicle_type}
          />

          <button
            type='button'
            className={cn(styles.HeaderMenuToggle, styles.Header_mobileOnly)}
            onClick={toggleMenu}
            aria-expanded={mobileMenuOpen}
            aria-label='Main menu'
            aria-controls='mobileMenu'
          >
            { !mobileMenuOpen && <img src='/static/images/admin/navigation/menu.svg' alt='Toggle Menu' width='16' height='16' /> }
            { mobileMenuOpen && <img src='/static/images/admin/x-black.svg' alt='Toggle Menu' width='16' height='16' /> }
          </button>
        </nav>

      </div>

      {/* Food Trucks mega-menu — always in DOM for SEO */}
      <div
        id='foodTrucksMenu'
        className={cn(styles.MegaMenuWrapper, {
          [styles.MegaMenuWrapper__open]: openMenu === 'foodTrucks',
        })}
        aria-hidden={openMenu !== 'foodTrucks'}
        onMouseEnter={() => handleMenuEnter('foodTrucks')}
        onMouseLeave={handleMenuLeave}
      >
        <div className='Section'>
          <FoodTrucksMenu cityName={city_name} vehicleType={vehicle_type} />
        </div>
      </div>

      {/* Catering mega-menu — always in DOM for SEO */}
      <div
        id='cateringMenu'
        className={cn(styles.MegaMenuWrapper, {
          [styles.MegaMenuWrapper__open]: openMenu === 'catering',
        })}
        aria-hidden={openMenu !== 'catering'}
        onMouseEnter={() => handleMenuEnter('catering')}
        onMouseLeave={handleMenuLeave}
      >
        <div className='Section'>
          <CateringMenu vehicleType={vehicle_type} />
        </div>
      </div>

      { isClient && isMobile &&
        <div
          className={cn(styles.MobileMenu_Backdrop, {
            [styles.MobileMenu_Backdrop__visible]: mobileMenuOpen,
          })}
          onClick={toggleMenu}
        />
      }

      <MobileMenu
        mobileMenuOpen={mobileMenuOpen}
        cityName={city_name}
        vehicleType={vehicle_type}
      />

    </header>
    </>
  );
}

export default Header;
